import { useEffect, useMemo, useState } from 'react';
import ReactCrop, { type Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import Button from '@cartwhl/ui/input/Button';

import useBreakpoint from 'hooks/useBreakpoint';
import { ProgressInfo } from 'hooks/useFfmpeg';

import { EditableImage, ImageEditSettings } from '../types';
import {
  absoluteCropToPercentageCrop,
  mirrorCrop,
  percentageCropToAbsoluteCrop,
  rotateCropBy90Degrees,
} from '../utils';
import LoadingOverlay from './LoadingOverlay';

export default function Cropping({
  defaultEditValues = {
    flip: 0,
    mirror: false,
  },
  close,
  next,
  inputImage,
  onMirrorOrFlipChange,
  progress,
}: {
  defaultEditValues?: ImageEditSettings;
  close: () => void;
  next: (editSettings: ImageEditSettings) => void | Promise<void>;
  inputImage: EditableImage;
  onMirrorOrFlipChange?: (editSettings: ImageEditSettings) => void;
  progress?: ProgressInfo | null;
}) {
  const [cropPercent, setCropPercent] = useState<Crop | undefined>(
    absoluteCropToPercentageCrop(defaultEditValues.crop, inputImage.metadata),
  );
  const [localEditValues, setLocalEditValues] =
    useState<ImageEditSettings>(defaultEditValues);

  const { isMobile } = useBreakpoint(1024);
  const submitText = isMobile ? 'Upload' : 'Upload image';

  const imageUrl = useMemo(() => {
    if (inputImage) return URL.createObjectURL(inputImage.blob);
  }, [inputImage]);

  useEffect(() => {
    // let the browser know not to keep the reference to the file any longer
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  const handleImageSubmission = async () => {
    next({
      ...localEditValues,
      crop: percentageCropToAbsoluteCrop(cropPercent, inputImage.metadata),
    });
  };

  const handleFlipClick = () => {
    setLocalEditValues((prev) => {
      // Cycle through angles: 0° -> 90° -> 180° -> 270° -> 0°
      let flip: 0 | 90 | 180 | 270 = 0;
      switch (prev.flip ?? 0) {
        case 0:
          flip = 90;
          break;
        case 90:
          flip = 180;
          break;
        case 180:
          flip = 270;
          break;
      }

      // Rotate the crop percent
      setCropPercent((prev) =>
        rotateCropBy90Degrees(prev, localEditValues.mirror),
      );

      const update = {
        ...prev,
        flip,
      };

      // Notify parent component if callback is provided
      if (onMirrorOrFlipChange) {
        onMirrorOrFlipChange(update);
      }

      return update;
    });
  };

  const handleMirrorClick = () => {
    setLocalEditValues((prev) => {
      setCropPercent(mirrorCrop(cropPercent));

      const update = {
        ...prev,
        mirror: !prev.mirror,
      };

      // Notify parent component if callback is provided
      if (onMirrorOrFlipChange) {
        onMirrorOrFlipChange(update);
      }

      return update;
    });
  };

  return (
    <div className="flex h-full flex-col">
      <div className="bg-gray-15 dark:bg-gray-75 dark:border-gray-60 border-gray-30 relative flex min-h-0 flex-1 flex-col rounded-lg border">
        {progress && <LoadingOverlay progress={progress} />}
        <div className="bg-gray-90 relative flex h-full min-h-0 w-full flex-1 items-center justify-center overflow-hidden rounded-t-lg dark:bg-black">
          {imageUrl && inputImage && (
            <>
              <div
                className="relative flex max-h-full max-w-full items-center justify-center"
                style={{
                  aspectRatio: `${inputImage.metadata.width}/${inputImage.metadata.height}`,
                }}
              >
                <ReactCrop
                  crop={
                    cropPercent || {
                      x: 0,
                      y: 0,
                      width: 100,
                      height: 100,
                      unit: '%',
                    }
                  }
                  onChange={(_, crop) => setCropPercent(crop)}
                  minHeight={50}
                  minWidth={50}
                  keepSelection
                >
                  <img
                    src={imageUrl}
                    alt="Upload preview"
                    className="h-auto max-h-full w-auto max-w-full"
                    style={{
                      display: 'block',
                    }}
                  />
                </ReactCrop>
              </div>

              {!isMobile && (
                <div className="absolute bottom-4 left-0 right-0">
                  <Options
                    onMirrorClick={handleMirrorClick}
                    onFlipClick={handleFlipClick}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isMobile && (
        <div className="mt-4">
          <Options
            onMirrorClick={handleMirrorClick}
            onFlipClick={handleFlipClick}
          />
        </div>
      )}

      <div className="mt-4 flex flex-none flex-wrap items-center justify-between gap-4 md:mt-6 md:gap-8">
        <Button onClick={close} className="order-1 sm:order-none">
          Cancel
        </Button>

        <div className="order-2 flex gap-2 sm:order-none">
          <Button
            variant="primary"
            onClick={async () => {
              await handleImageSubmission();
            }}
          >
            {submitText}
          </Button>
        </div>
      </div>
    </div>
  );
}

const Options = ({
  onMirrorClick,
  onFlipClick,
}: {
  onMirrorClick: () => void;
  onFlipClick: () => void;
}) => {
  return (
    <div className="divide-gray-30 dark:divide-gray-60 flex items-center divide-x lg:justify-center">
      <Button
        icon={<MirrorIcon />}
        variant="secondary"
        className="flex-1 rounded-r-none lg:flex-none"
        onClick={onMirrorClick}
      >
        Mirror
      </Button>
      <Button
        icon={<RotateIcon />}
        variant="secondary"
        className="flex-1 rounded-l-none lg:flex-none"
        onClick={onFlipClick}
      >
        Flip
      </Button>
    </div>
  );
};

const RotateIcon = () => {
  return (
    <svg
      width="21"
      height="21"
      viewBox="0 0 21 21"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M15.1006 18.8677H6.89111C6.13851 18.8677 5.56852 18.6768 5.18115 18.2949C4.79378 17.9186 4.6001 17.3486 4.6001 16.585V8.38379C4.6001 7.61458 4.79378 7.04183 5.18115 6.66553C5.56852 6.28369 6.13851 6.09277 6.89111 6.09277H15.1006C15.8587 6.09277 16.4287 6.28369 16.8105 6.66553C17.1979 7.04183 17.3916 7.61458 17.3916 8.38379V16.585C17.3916 17.3486 17.1979 17.9186 16.8105 18.2949C16.4287 18.6768 15.8587 18.8677 15.1006 18.8677ZM15.0093 17.2739C15.286 17.2739 15.4852 17.2131 15.6069 17.0913C15.7342 16.9751 15.7979 16.7731 15.7979 16.4854V8.4751C15.7979 8.18734 15.7342 7.98535 15.6069 7.86914C15.4852 7.7474 15.286 7.68652 15.0093 7.68652H6.99072C6.71956 7.68652 6.51758 7.7474 6.38477 7.86914C6.25749 7.98535 6.19385 8.18734 6.19385 8.4751V16.4854C6.19385 16.7731 6.25749 16.9751 6.38477 17.0913C6.51758 17.2131 6.71956 17.2739 6.99072 17.2739H15.0093ZM1.07227 7.94385C0.911784 7.94385 0.773438 7.88574 0.657227 7.76953C0.535482 7.64779 0.474609 7.50667 0.474609 7.34619V6.03467C0.474609 5.22119 0.648926 4.50456 0.997559 3.88477C1.34066 3.26497 1.81934 2.78353 2.43359 2.44043C3.04785 2.0918 3.76172 1.91748 4.5752 1.91748H4.83252V0.879883C4.83252 0.558919 4.93766 0.362467 5.14795 0.290527C5.35824 0.213053 5.58512 0.262858 5.82861 0.439941L8.01172 2.05859C8.1888 2.19694 8.27734 2.34359 8.27734 2.49854C8.27734 2.64795 8.1888 2.78906 8.01172 2.92188L5.82861 4.53223C5.58512 4.70931 5.35824 4.75911 5.14795 4.68164C4.93766 4.60417 4.83252 4.41048 4.83252 4.10059V3.03809H4.5752C3.98861 3.03809 3.47673 3.16813 3.03955 3.42822C2.60791 3.68278 2.27311 4.03695 2.03516 4.49072C1.7972 4.93896 1.67822 5.45361 1.67822 6.03467V7.34619C1.67822 7.50667 1.62012 7.64779 1.50391 7.76953C1.38216 7.88574 1.23828 7.94385 1.07227 7.94385Z" />
    </svg>
  );
};

const MirrorIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path d="M7.80086 1.0319C7.46146 0.960444 7.10777 1.00989 6.80097 1.17167C6.49418 1.33346 6.2536 1.5974 6.12086 1.91784L6.11524 1.93096L0.115236 16.9244C0.0202409 17.1526 -0.0169092 17.4006 0.00708877 17.6466C0.0310867 17.8926 0.115489 18.1288 0.252792 18.3343C0.390095 18.5398 0.576044 18.7082 0.794109 18.8245C1.01217 18.9408 1.2556 19.0014 1.50274 19.001H7.50274C7.90056 19.001 8.28209 18.8429 8.5634 18.5616C8.8447 18.2803 9.00274 17.8988 9.00274 17.501V2.50096C9.00519 2.15436 8.8864 1.8178 8.66692 1.54953C8.44744 1.28126 8.14108 1.09815 7.80086 1.0319ZM7.50274 17.501H1.50274L1.50836 17.4869L7.50274 2.50096V17.501ZM19.3874 16.9244L13.3874 1.93096L13.3818 1.91784C13.2461 1.60069 13.0053 1.34001 12.6998 1.1797C12.3944 1.01939 12.043 0.969248 11.705 1.03772C11.3669 1.1062 11.0627 1.28911 10.8437 1.55563C10.6248 1.82216 10.5043 2.15602 10.5027 2.50096V17.501C10.5027 17.8988 10.6608 18.2803 10.9421 18.5616C11.2234 18.8429 11.6049 19.001 12.0027 19.001H18.0027C18.2497 19.0011 18.4929 18.9403 18.7107 18.8238C18.9285 18.7074 19.1142 18.539 19.2513 18.3335C19.3884 18.1281 19.4726 17.892 19.4966 17.6461C19.5205 17.4003 19.4833 17.1524 19.3884 16.9244H19.3874ZM12.0027 17.501V2.50096L17.9962 17.486L18.0018 17.5L12.0027 17.501Z" />
    </svg>
  );
};
